import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import Pager from "../../components/Pager";
import { errorMessage } from "../../services";
import { confirmPayment, failPayment, getPaymentHistory } from "../../services/admin";
import { dateText, money } from "../../utils/format";
import DataTable from "../../components/DataTable";
import Loader, { Spinner } from "../../components/Loader";

// Individual payment records. The Super Admin can confirm or reject payments that are still pending.
function ConfirmModal({ payment, onClose, onConfirmed, notify }) {
  const [method, setMethod] = useState("UPI");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await confirmPayment(payment._id, { method, reference: reference.trim() });
      notify("Payment confirmed and applied");
      onConfirmed();
    } catch (err) {
      notify(errorMessage(err), "error");
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="PAYMENT" title={`Confirm ${money(payment.amount)}`} onClose={onClose} description="Only confirm after the money has been received.">
      <form onSubmit={submit}>
        <label className="admin-field">How was it paid?
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option>UPI</option><option>Bank transfer</option><option>Card</option><option>Cash</option><option>Other</option>
          </select>
        </label>
        <label className="admin-field">Reference / transaction id<input value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} /></label>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Confirming</> : "Confirm payment"}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function PaymentHistory({ user, notify, onChanged }) {
  const isSuper = user.role === "superadmin";
  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [status, setStatus] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getPaymentHistory({ status, page: pageNumber, limit: 10 });
      setPayments(data.payments);
      setMeta({ total: data.total, page: data.page, pages: data.pages });
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
    setLoaded(true);
  }, [status, pageNumber]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); if (onChanged) onChanged(); };

  const fail = async (payment) => {
    try {
      await failPayment(payment._id);
      notify("Payment marked as failed");
      refresh();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <div className="panel table-panel tab-gap">
      <div className="panel-heading">
        <div><h2>Payment history</h2><p>Every payment and bulk purchase.</p></div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPageNumber(1); }} aria-label="Filter payments">
          <option value="">All payments</option><option value="pending">Waiting for payment</option><option value="paid">Paid</option><option value="failed">Failed</option>
        </select>
      </div>
      {error && <div className="admin-error">{error}</div>}
      <DataTable>
          <thead><tr><th>DATE</th><th>FOR</th><th>PLAN</th><th>AMOUNT</th><th>STATUS</th><th>METHOD</th>{isSuper && <th>ACTIONS</th>}</tr></thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p._id}>
                <td>{dateText(p.createdAt)}</td>
                <td>{p.type === "bulk" ? `${p.quantity} website licences` : p.storeName || "Website"}</td>
                <td>{p.plan} · {p.months} {p.months === 1 ? "month" : "months"}</td>
                <td><b>{money(p.amount)}</b></td>
                <td><span className={`plan-badge badge-${p.status}`}>{p.status === "pending" ? "waiting for payment" : p.status}</span></td>
                <td>{p.method || "—"}{p.reference ? <small className="cell-sub">{p.reference}</small> : null}</td>
                {isSuper && (
                  <td>
                    {p.status === "pending" && (
                      <div className="row-actions">
                        <button className="pay-action" onClick={() => setConfirming(p)}>Confirm</button>
                        <button className="toggle-action" onClick={() => fail(p)}>Reject</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {!payments.length && <tr><td colSpan={isSuper ? 7 : 6} className="empty-row">{loaded ? "No payments yet." : <Loader label="Loading payments" />}</td></tr>}
          </tbody>
      </DataTable>
      <Pager page={meta.page} pages={meta.pages} total={meta.total} noun="payments" onPage={setPageNumber} />
      {confirming && <ConfirmModal payment={confirming} notify={notify} onClose={() => setConfirming(null)} onConfirmed={() => { setConfirming(null); refresh(); }} />}
    </div>
  );
}
